import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormLabel,
  RadioButtonGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  TimePicker,
} from '@roger/ui-components';
import { FileText, Loader2, Trash2, Upload, X } from 'lucide-react';
import {
  completeTaskOutsideRecord,
  deleteTaskOutsideRecordDocument,
  getTaskDocumentUrl,
  getTaskOutsideRecord,
  updateTaskOutsideRecord,
  uploadTaskOutsideRecordDocument,
} from '../../services/api';
import type { OutsideRecordDetailsResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { selectAttestationForFamily } from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { outsideRecordByline } from '../task/outsideRecord';

export interface OutsideRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  /** Called on close when anything was saved, so the visit can be reloaded. */
  onChanged?: () => void | Promise<void>;
}

interface TimesDraft {
  /** YYYY-MM-DD, or empty. */
  date: string;
  /** HH:MM, or empty. */
  timeIn: string;
  timeOut: string;
  /** End-of-care visits only: the FL17 code, and the discharge YYYY-MM-DD. */
  patientStatus: string;
  dischargeDate: string;
  /** Transfer visits only: whether the transfer discharged the patient. */
  transferDischarged: '' | 'yes' | 'no';
}

const EMPTY_TIMES: TimesDraft = {
  date: '',
  timeIn: '',
  timeOut: '',
  patientStatus: '',
  dischargeDate: '',
  transferDischarged: '',
};
const TRANSFER_DISCHARGED_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
const MAX_REASON_LENGTH = 2000;
const ACCEPTED_FILES = '.pdf,.png,.jpg,.jpeg,.doc,.docx';
const SAVE_FAILED = 'Could not save the visit.';
const UPLOAD_FAILED = 'Could not upload the attachment.';

const datePart = (value: string | null | undefined): string =>
  (value ?? '').split('T')[0];
// The server and the picker may send "HH:MM:SS"; the API takes "HH:MM".
const hhmm = (value: string | null | undefined): string =>
  (value ?? '').slice(0, 5);

function toTimesDraft(details: OutsideRecordDetailsResponse): TimesDraft {
  return {
    date: datePart(
      details.visit_date ??
        (details.outside_record ? null : details.scheduled_date)
    ),
    timeIn: hhmm(details.time_in),
    timeOut: hhmm(details.time_out),
    patientStatus: details.patient_status ?? '',
    dischargeDate: datePart(details.end_of_care_date),
    // Before completion the prefilled M0100 is not a choice, so it starts blank.
    transferDischarged: !details.outside_record
      ? ''
      : details.transfer_discharged === true
        ? 'yes'
        : details.transfer_discharged === false
          ? 'no'
          : '',
  };
}

const sameTimes = (a: TimesDraft, b: TimesDraft): boolean =>
  a.date === b.date &&
  a.timeIn === b.timeIn &&
  a.timeOut === b.timeOut &&
  a.patientStatus === b.patientStatus &&
  a.dischargeDate === b.dischargeDate &&
  a.transferDischarged === b.transferDischarged;

/**
 * Completes a Not Started visit that was performed and documented outside
 * Roger, or corrects the date, times and attachments of one already completed
 * that way. The server checks the outside-record permission on every route.
 */
export function OutsideRecordDialog({
  open,
  onOpenChange,
  taskId,
  onChanged,
}: OutsideRecordDialogProps) {
  const { success: showSuccess } = useToast();
  const { attestations } = useActiveAttestations();
  const [details, setDetails] = useState<OutsideRecordDetailsResponse | null>(
    null
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [times, setTimes] = useState<TimesDraft>(EMPTY_TIMES);
  const [reason, setReason] = useState('');
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const changedRef = useRef(false);
  const requestSeqRef = useRef(0);

  // `keepTimes` leaves an unsaved date/times edit in place after an attachment change.
  const load = useCallback(
    async ({ keepTimes = false } = {}) => {
      const seq = ++requestSeqRef.current;
      try {
        // The generated client does not throw; a refusal arrives as `error`.
        const result = await getTaskOutsideRecord({
          path: { task_id: taskId },
        });
        if (requestSeqRef.current !== seq) return;
        if (result.error || !result.data) {
          console.error('Failed to load the outside record', result.error);
          setLoadFailed(true);
          return;
        }
        setLoadFailed(false);
        setDetails(result.data);
        if (!keepTimes) setTimes(toTimesDraft(result.data));
      } catch (err) {
        if (requestSeqRef.current !== seq) return;
        console.error('Failed to load the outside record', err);
        setLoadFailed(true);
      }
    },
    [taskId]
  );

  useEffect(() => {
    if (!open) return;
    setDetails(null);
    setLoadFailed(false);
    setTimes(EMPTY_TIMES);
    setReason('');
    setPickedFiles([]);
    setError(null);
    changedRef.current = false;
    void load();
  }, [open, load]);

  const close = () => {
    onOpenChange(false);
    if (changedRef.current) {
      changedRef.current = false;
      void onChanged?.();
    }
  };

  const outsideRecord = details?.outside_record ?? null;
  const mode: 'complete' | 'edit' | 'refused' | null = !details
    ? null
    : outsideRecord
      ? 'edit'
      : details.can_complete
        ? 'complete'
        : 'refused';
  const busy = saving || uploading || deleting;

  const attestation = attestations
    ? (selectAttestationForFamily(attestations, 'outside_record') ?? null)
    : null;
  const timesFilled =
    times.date !== '' && times.timeIn !== '' && times.timeOut !== '';
  const timeOrderInvalid =
    times.timeIn !== '' &&
    times.timeOut !== '' &&
    times.timeOut <= times.timeIn;
  const timesChanged = details
    ? !sameTimes(times, toTimesDraft(details))
    : false;
  // An end-of-care visit also records the claim's patient status and the
  // discharge date, which defaults to the visit date. A transfer records
  // them only when it discharged the patient.
  const writable = mode === 'complete' || mode === 'edit';
  const endOfCare = details?.end_of_care === true;
  const isTransfer = endOfCare && details?.is_transfer === true;
  const takesStatus =
    endOfCare && (!isTransfer || times.transferDischarged === 'yes');
  const dischargeDate = times.dischargeDate || (writable ? times.date : '');
  const endOfCareFilled =
    (!isTransfer || times.transferDischarged !== '') &&
    (!takesStatus || (times.patientStatus !== '' && dischargeDate !== ''));
  const statusOptions = (details?.patient_status_options ?? []).map(option => ({
    value: option.code,
    label: `${option.label} (${option.code})`,
  }));
  if (
    times.patientStatus !== '' &&
    !statusOptions.some(option => option.value === times.patientStatus)
  ) {
    statusOptions.push({
      value: times.patientStatus,
      label: times.patientStatus,
    });
  }
  const endOfCareBody = {
    ...(isTransfer
      ? { transfer_discharged: times.transferDischarged === 'yes' }
      : {}),
    ...(takesStatus
      ? {
          patient_status: times.patientStatus,
          end_of_care_date: dischargeDate,
        }
      : {}),
  };
  const canSubmit =
    !busy &&
    timesFilled &&
    endOfCareFilled &&
    !timeOrderInvalid &&
    (mode === 'complete'
      ? reason.trim() !== '' && attestation !== null
      : mode === 'edit' && timesChanged);

  /** Uploads each file; returns the names of those that failed. */
  const uploadFiles = async (files: File[]): Promise<string[]> => {
    const failed: string[] = [];
    setUploading(true);
    try {
      for (const file of files) {
        try {
          const result = await uploadTaskOutsideRecordDocument({
            path: { task_id: taskId },
            body: { file },
          });
          if (result.error) {
            console.error('Failed to upload attachment', result.error);
            failed.push(file.name);
          } else {
            changedRef.current = true;
          }
        } catch (err) {
          console.error('Failed to upload attachment', err);
          failed.push(file.name);
        }
      }
    } finally {
      setUploading(false);
    }
    return failed;
  };

  const handleComplete = async () => {
    if (!canSubmit || !attestation) return;
    setSaving(true);
    setError(null);
    try {
      const result = await completeTaskOutsideRecord({
        path: { task_id: taskId },
        body: {
          visit_date: times.date,
          time_in: times.timeIn,
          time_out: times.timeOut,
          reason: reason.trim(),
          attestation_version: attestation.version,
          ...endOfCareBody,
        },
      });
      if (result.error) {
        console.error('Failed to complete with outside record', result.error);
        setError(getBusinessErrorMessage(result) ?? SAVE_FAILED);
        return;
      }
    } catch (err) {
      console.error('Failed to complete with outside record', err);
      setError(SAVE_FAILED);
      return;
    } finally {
      setSaving(false);
    }
    changedRef.current = true;
    const failed = await uploadFiles(pickedFiles);
    setPickedFiles([]);
    if (failed.length === 0) {
      showSuccess('Visit completed');
      close();
      return;
    }
    // The visit is completed either way; stay open so the upload can be retried.
    setError(
      `Visit completed, but ${failed.join(', ')} did not upload. Upload it again.`
    );
    await load();
  };

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const result = await updateTaskOutsideRecord({
        path: { task_id: taskId },
        body: {
          visit_date: times.date,
          time_in: times.timeIn,
          time_out: times.timeOut,
          ...endOfCareBody,
        },
      });
      if (result.error) {
        console.error('Failed to save the outside record', result.error);
        setError(getBusinessErrorMessage(result) ?? SAVE_FAILED);
        return;
      }
      changedRef.current = true;
      showSuccess('Visit saved');
      close();
    } catch (err) {
      console.error('Failed to save the outside record', err);
      setError(SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so picking the same file again fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return;
    if (mode === 'complete') {
      setPickedFiles(prev => [...prev, ...files]);
      return;
    }
    setError(null);
    const failed = await uploadFiles(files);
    if (failed.length > 0) setError(UPLOAD_FAILED);
    await load({ keepTimes: true });
  };

  const handleOpenDocument = async (documentId: string) => {
    try {
      const { data } = await getTaskDocumentUrl({
        path: { task_id: taskId, document_id: documentId },
        throwOnError: true,
      });
      if (!data?.url) throw new Error('Document URL missing from response');
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Failed to get document URL', err);
      setError('Could not open the attachment.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteTaskOutsideRecordDocument({
        path: { task_id: taskId, document_id: pendingDeleteId },
      });
      if (result.error) {
        console.error('Failed to delete attachment', result.error);
        setError(
          getBusinessErrorMessage(result) ?? 'Could not delete the attachment.'
        );
      } else {
        changedRef.current = true;
      }
    } catch (err) {
      console.error('Failed to delete attachment', err);
      setError('Could not delete the attachment.');
    } finally {
      setDeleting(false);
      setPendingDeleteId(null);
    }
    await load({ keepTimes: true });
  };

  const timesEditable = writable && !busy;

  return (
    <Dialog
      open={open}
      // Closing mid-upload would skip the visit reload, so wait for it.
      onOpenChange={next => (next || busy ? undefined : close())}
    >
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Outside record</DialogTitle>
          <DialogDescription>
            A visit performed and documented outside Roger.
          </DialogDescription>
        </DialogHeader>

        {!details ? (
          loadFailed ? (
            <div className='space-y-3 py-2'>
              <p className='text-sm text-destructive'>
                Couldn&apos;t load the visit.
              </p>
              <Button size='sm' variant='outline' onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : (
            <div
              className='flex items-center gap-2 py-4 text-sm text-muted-foreground'
              role='status'
            >
              <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
              Loading...
            </div>
          )
        ) : mode === 'refused' ? (
          <p className='text-sm text-muted-foreground'>
            This visit can&apos;t be completed with an outside record.
          </p>
        ) : (
          // min-w-0: the dialog lays out as a grid, and a long file name
          // would otherwise widen the column past the dialog's right edge.
          <div className='min-w-0 space-y-5'>
            {outsideRecord && (
              <p className='text-sm text-muted-foreground'>
                {outsideRecord.reason} ({outsideRecordByline(outsideRecord)})
              </p>
            )}

            <div className='space-y-3'>
              <div className='space-y-2'>
                <FormLabel htmlFor='outside-record-date' required>
                  Visit date
                </FormLabel>
                <DatePicker
                  id='outside-record-date'
                  aria-label='Visit date'
                  date={parseISOToLocalDate(times.date)}
                  onDateChange={date =>
                    setTimes(prev => ({
                      ...prev,
                      date: date ? formatDateToISO(date) : '',
                    }))
                  }
                  minDate={parseISOToLocalDate(details.episode_start_date)}
                  maxDate={parseISOToLocalDate(details.episode_end_date)}
                  allowClear={false}
                  disabled={!timesEditable}
                  datePickerSize='default'
                  className='w-full'
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-2'>
                  <FormLabel htmlFor='outside-record-time-in' required>
                    Time in
                  </FormLabel>
                  <TimePicker
                    id='outside-record-time-in'
                    time={times.timeIn || undefined}
                    onTimeChange={time =>
                      setTimes(prev => ({ ...prev, timeIn: hhmm(time) }))
                    }
                    disabled={!timesEditable}
                    timePickerSize='default'
                    hourCycle='24'
                    className='w-full'
                  />
                </div>
                <div className='space-y-2'>
                  <FormLabel htmlFor='outside-record-time-out' required>
                    Time out
                  </FormLabel>
                  <TimePicker
                    id='outside-record-time-out'
                    time={times.timeOut || undefined}
                    onTimeChange={time =>
                      setTimes(prev => ({ ...prev, timeOut: hhmm(time) }))
                    }
                    error={timeOrderInvalid}
                    disabled={!timesEditable}
                    timePickerSize='default'
                    hourCycle='24'
                    className='w-full'
                  />
                </div>
              </div>
              {timeOrderInvalid && (
                <p className='text-xs text-destructive'>
                  Time out must be after time in.
                </p>
              )}
              {isTransfer && (
                <div className='space-y-2'>
                  <FormLabel
                    id='outside-record-transfer-discharged-label'
                    required
                  >
                    Was the patient discharged?
                  </FormLabel>
                  <RadioButtonGroup
                    aria-labelledby='outside-record-transfer-discharged-label'
                    options={TRANSFER_DISCHARGED_OPTIONS}
                    value={times.transferDischarged}
                    onValueChange={value =>
                      setTimes(prev => ({
                        ...prev,
                        transferDischarged: value === 'yes' ? 'yes' : 'no',
                      }))
                    }
                    disabled={!timesEditable}
                    orientation='horizontal'
                  />
                </div>
              )}
              {takesStatus && (
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <div className='min-w-0 space-y-2'>
                    <FormLabel htmlFor='outside-record-patient-status' required>
                      Patient status
                    </FormLabel>
                    <Select
                      value={times.patientStatus}
                      onValueChange={patientStatus =>
                        setTimes(prev => ({ ...prev, patientStatus }))
                      }
                      disabled={!timesEditable}
                    >
                      <SelectTrigger
                        id='outside-record-patient-status'
                        selectSize='default'
                        className='w-full'
                      >
                        <SelectValue placeholder='Select status' />
                      </SelectTrigger>
                      <SelectContent className='w-[var(--radix-select-trigger-width)]'>
                        {statusOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <FormLabel htmlFor='outside-record-discharge-date' required>
                      Discharge date
                    </FormLabel>
                    <DatePicker
                      id='outside-record-discharge-date'
                      aria-label='Discharge date'
                      date={parseISOToLocalDate(dischargeDate)}
                      onDateChange={date =>
                        setTimes(prev => ({
                          ...prev,
                          dischargeDate: date ? formatDateToISO(date) : '',
                        }))
                      }
                      minDate={parseISOToLocalDate(details.episode_start_date)}
                      maxDate={parseISOToLocalDate(details.episode_end_date)}
                      allowClear={false}
                      disabled={!timesEditable}
                      datePickerSize='default'
                      className='w-full'
                    />
                  </div>
                </div>
              )}
            </div>

            {mode === 'complete' && (
              <div className='space-y-2'>
                <FormLabel htmlFor='outside-record-reason' required>
                  Reason
                </FormLabel>
                <Textarea
                  id='outside-record-reason'
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder='e.g. Performed by Step Up Therapy; note to follow.'
                  maxLength={MAX_REASON_LENGTH}
                  rows={3}
                  disabled={busy}
                />
                <p className='whitespace-pre-wrap text-xs text-muted-foreground'>
                  {attestation?.text ?? 'Loading attestation…'}
                </p>
              </div>
            )}

            <div className='space-y-2'>
              <div className='flex items-center justify-between gap-2'>
                <h3 className='text-sm font-medium leading-none'>
                  Attachments
                </h3>
                {uploading && (
                  <Loader2
                    className='h-4 w-4 animate-spin text-muted-foreground'
                    aria-label='Uploading'
                  />
                )}
              </div>
              {mode === 'complete' ? (
                pickedFiles.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    No attachments.
                  </p>
                ) : (
                  <ul className='divide-y'>
                    {pickedFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className='flex items-center gap-2 py-2 text-sm'
                      >
                        <FileText
                          className='h-4 w-4 shrink-0 text-muted-foreground'
                          aria-hidden='true'
                        />
                        <span className='min-w-0 flex-1 truncate'>
                          {file.name}
                        </span>
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() =>
                            setPickedFiles(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          disabled={busy}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className='h-4 w-4' aria-hidden='true' />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )
              ) : details.documents.length === 0 ? (
                <p className='text-sm text-muted-foreground'>No attachments.</p>
              ) : (
                <ul className='divide-y'>
                  {details.documents.map(doc => (
                    <li key={doc.id} className='flex items-center gap-2 py-2'>
                      <button
                        type='button'
                        className='flex min-w-0 flex-1 items-center gap-2 text-left text-sm hover:underline'
                        onClick={() => void handleOpenDocument(doc.id)}
                      >
                        <FileText
                          className='h-4 w-4 shrink-0 text-muted-foreground'
                          aria-hidden='true'
                        />
                        <span className='truncate'>{doc.file_name}</span>
                      </button>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => setPendingDeleteId(doc.id)}
                        disabled={busy}
                        aria-label={`Delete ${doc.file_name}`}
                      >
                        <Trash2
                          className='h-4 w-4 text-destructive'
                          aria-hidden='true'
                        />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={fileInputRef}
                type='file'
                multiple
                className='hidden'
                onChange={e => void handleFilesPicked(e)}
                accept={ACCEPTED_FILES}
                aria-label='Add attachment'
              />
              <Button
                size='sm'
                variant='outline'
                className='w-full sm:w-auto'
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <Upload className='h-4 w-4' aria-hidden='true' />
                Add attachment
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={close} disabled={busy}>
            {mode === 'complete' ? 'Cancel' : 'Close'}
          </Button>
          {(mode === 'complete' || mode === 'edit') && (
            <Button
              onClick={() =>
                void (mode === 'complete' ? handleComplete() : handleSave())
              }
              disabled={!canSubmit}
            >
              {saving && (
                <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
              )}
              {mode === 'complete' ? 'Complete with outside record' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ConfirmationDialog
        open={pendingDeleteId !== null}
        title='Delete attachment'
        message='Delete this attachment? This cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        loading={deleting}
        onAccept={() => void handleDeleteConfirm()}
        onCancel={() => setPendingDeleteId(null)}
      />
    </Dialog>
  );
}
